import { useApolloClient } from '@apollo/client';
import type { FC } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import TextField from '@app/components/input/TextField';
import { getLocalizationCountryCookie } from '@app/helpers/localization';
import useValidationMessage from '@app/hooks/useValidationMessage';

import { AddressFormFields } from '../Address.constants';
import messages from '../Address.messages';
import { VAT_NUMBER_VALIDITY_QUERY } from './AddressForm.queries';
import type {
  VatNumberValidityQuery,
  VatNumberValidityQueryVariables,
} from './__generated__/VatNumberValidityQuery';

export const VatNumberField: FC<{ namespace: string }> = ({ namespace }) => {
  const apolloClient = useApolloClient();
  const {
    formState: { errors },
    register,
  } = useFormContext();
  const { msg } = useValidationMessage(messages);

  const [isValidating, setIsValidating] = useState(false);
  const selectedCountryCode = getLocalizationCountryCookie();

  const validate = async (vatNumber: string) => {
    if (!vatNumber) return true;

    setIsValidating(true);

    const response = await apolloClient.query<
      VatNumberValidityQuery,
      VatNumberValidityQueryVariables
    >({
      query: VAT_NUMBER_VALIDITY_QUERY,
      variables: { countryCode: selectedCountryCode, vatNumber },
    });

    const data = response.data.magento2?.checkVatNumber;

    setIsValidating(false);

    return data?.isValid || msg({ type: 'invalid', key: 'vatNumber' });
  };

  return (
    <TextField
      {...register(`${namespace}.${AddressFormFields.VatNumber}`, { validate })}
      data-testid={`${namespace}-vat`}
      autoComplete="nope"
      loading={isValidating}
      maxLength={128}
      isValid={!errors[namespace]?.[AddressFormFields.VatNumber]}
      formLabel={
        <>
          <FormattedMessage {...messages.vatNumber} /> (
          <FormattedMessage {...messages.optional} />)
        </>
      }
    />
  );
};
